import { getTenantConfig } from '../../config/tenantConfig';
import httpClient from './httpClient';

/**
 * Sender Registry API Service
 * Handles email sender registration management
 * Uses httpClient for automatic 401 handling and token refresh
 */

/**
 * Get all registered senders
 * @param {Object} options - Query options
 * @param {string} options.tenantId - Filter by tenant ID
 * @param {boolean} options.enabled - Filter by enabled status
 * @returns {Promise<Array>} Array of sender objects
 */
export async function listSenders(options = {}) {
  try {
    const config = getTenantConfig();
    const senderRegistryApi = config.api.senderRegistry;

    if (!senderRegistryApi) {
      console.error('Sender Registry API endpoint not configured');
      throw new Error('Sender Registry API not configured. Run bin/frontend/configure-api-endpoints.sh');
    }

    // Build query params
    const params = {};
    if (options.tenantId) params.tenantId = options.tenantId;
    if (options.enabled !== undefined) params.enabled = options.enabled;

    const result = await httpClient.get(`${senderRegistryApi}/senders`, { params });
    return result.senders || [];
  } catch (error) {
    console.error('Error fetching senders:', error);
    throw error;
  }
}

/**
 * Get a specific sender by ID
 * @param {string} senderId - Sender email address
 * @returns {Promise<Object>} Sender object
 */
export async function getSender(senderId) {
  try {
    const config = getTenantConfig();
    const senderRegistryApi = config.api.senderRegistry;

    if (!senderRegistryApi) {
      throw new Error('Sender Registry API not configured');
    }

    return await httpClient.get(`${senderRegistryApi}/senders/${encodeURIComponent(senderId)}`);
  } catch (error) {
    console.error(`Error fetching sender ${senderId}:`, error);
    throw error;
  }
}

/**
 * Register a new sender
 * @param {Object} senderData - Sender registration data
 * @param {string} senderData.senderId - Sender email address (e.g., user@example.com)
 * @param {string} senderData.tenantId - Tenant ID this sender belongs to
 * @param {string} senderData.emailAddress - Recipient email address (e.g., rec001@cameras.kwari.com)
 * @param {number} senderData.dailyUploadLimit - Daily upload limit (optional, default from backend)
 * @param {boolean} senderData.enabled - Whether sender is enabled (optional, default true)
 * @param {boolean} senderData.autoMatch - Whether to auto-match uploads (optional, default true)
 * @returns {Promise<Object>} Created sender object
 */
export async function createSender(senderData) {
  try {
    const config = getTenantConfig();
    const senderRegistryApi = config.api.senderRegistry;

    if (!senderRegistryApi) {
      throw new Error('Sender Registry API not configured');
    }

    return await httpClient.post(`${senderRegistryApi}/senders`, senderData);
  } catch (error) {
    console.error('Error creating sender:', error);
    throw error;
  }
}

/**
 * Update sender settings
 * @param {string} senderId - Sender email address
 * @param {Object} updates - Fields to update (emailAddress, enabled, autoMatch, dailyUploadLimit)
 * @returns {Promise<Object>} Updated sender object
 */
export async function updateSender(senderId, updates) {
  try {
    const config = getTenantConfig();
    const senderRegistryApi = config.api.senderRegistry;

    if (!senderRegistryApi) {
      throw new Error('Sender Registry API not configured');
    }

    return await httpClient.put(`${senderRegistryApi}/senders/${encodeURIComponent(senderId)}`, updates);
  } catch (error) {
    console.error(`Error updating sender ${senderId}:`, error);
    throw error;
  }
}

/**
 * Delete a sender registration
 * @param {string} senderId - Sender email address
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteSender(senderId) {
  try {
    const config = getTenantConfig();
    const senderRegistryApi = config.api.senderRegistry;

    if (!senderRegistryApi) {
      throw new Error('Sender Registry API not configured');
    }

    return await httpClient.delete(`${senderRegistryApi}/senders/${encodeURIComponent(senderId)}`);
  } catch (error) {
    console.error(`Error deleting sender ${senderId}:`, error);
    throw error;
  }
}

/**
 * Reset upload counter for a sender
 * @param {string} senderId - Sender email address
 * @returns {Promise<Object>} Updated sender object
 */
export async function resetSenderCounter(senderId) {
  try {
    const config = getTenantConfig();
    const senderRegistryApi = config.api.senderRegistry;

    if (!senderRegistryApi) {
      throw new Error('Sender Registry API not configured');
    }

    return await httpClient.post(`${senderRegistryApi}/senders/${encodeURIComponent(senderId)}/reset-counter`);
  } catch (error) {
    console.error(`Error resetting counter for sender ${senderId}:`, error);
    throw error;
  }
}
